import { RewardFieldsBE } from "types/rewards";
import { transformTranslationsToObject } from "../RewardForm/rewardFormHelpers";

export const AVAILABILITY_LIMIT_WARNING_VALUE = 0.2;
export const UNLIMITED_AVAILABILITY = -1;

export const formatUsageLeft = (usageLeft?: number) =>
  usageLeft && usageLeft >= 0 ? usageLeft : -Infinity;

export const formatUsageLimitGeneral = (usageGeneral: number) =>
  usageGeneral === UNLIMITED_AVAILABILITY ? -Infinity : usageGeneral;

export const isGeneralUsageUnlimited = (usageGeneral: number) =>
  usageGeneral === UNLIMITED_AVAILABILITY;

// usageLeft is unlimited also if the field does not exist in the reward object
export const isUsageLeftUnlimited = (usageLeft?: number) =>
  usageLeft === UNLIMITED_AVAILABILITY || usageLeft === undefined;

export const countAvailabilityValue = (
  usageLeft: number,
  usageGeneral: number,
) => {
  // If the type is other than number, it returns an undefined value.
  // Otherwise it may return a decimal/integer/-Infinity/NaN.
  if (!(typeof usageLeft === "number" && typeof usageGeneral === "number")) {
    return;
  }

  if (usageLeft === 0 && usageGeneral === 0) {
    return 0;
  } else {
    return usageLeft / usageGeneral;
  }
};

export const countUsageLeftProgressValue = (
  usageLeft: number,
  usageGeneral: number,
) => {
  // pattern: usageLeft/usageGeneral

  // If usageLeft has fulfilled progress, only the darker color assigned to usageLeft is visible in the progress bar.
  const fulfilledProgress = 1;

  // If usageLeft has not any progress, darker color assigned to usageLeft is NOT visible in the progress bar.
  const emptyProgress = 0;

  // 0/0 - bright progress bar
  if (usageLeft === 0 && usageGeneral === 0) {
    return emptyProgress;
    // 0/∞ - bright progress bar
  } else if (usageLeft === 0 && usageGeneral === -Infinity) {
    return emptyProgress;
    // ∞/∞ - dark progress bar
  } else if (usageLeft === -Infinity && usageGeneral === -Infinity) {
    return fulfilledProgress;
  } else if (
    // e.g. 2/∞ - dark progress bar
    usageLeft !== 0 &&
    usageLeft !== -Infinity &&
    usageGeneral === -Infinity
  ) {
    return fulfilledProgress;
  }

  // e.g. 2/5, usageLeft and usageGeneral are integers
  return usageLeft;
};

export const countUsageGeneralProgressValue = (
  usageLeft: number,
  usageGeneral: number,
) => {
  // pattern: usageLeft/usageGeneral

  // If usageGeneral has fulfilled progress, only the brighter color assigned to usageGeneral is visible in the progress bar.
  const fulfilledProgress = 1;

  // If usageGeneral has not any progress, brighter color assigned to usageGeneral is NOT visible in the progress bar.
  const emptyProgress = 0;

  // 0/0 - bright progress bar
  if (usageLeft === 0 && usageGeneral === 0) {
    return fulfilledProgress;
    // 0/∞ - bright progress bar
  } else if (usageLeft === 0 && usageGeneral === -Infinity) {
    return fulfilledProgress;
    // ∞/∞ - dark progress bar
  } else if (usageLeft === -Infinity && usageGeneral === -Infinity) {
    return emptyProgress;
  } else if (
    // e.g. 2/∞ - dark progress bar
    usageLeft !== 0 &&
    usageLeft !== -Infinity &&
    usageGeneral === -Infinity
  ) {
    return emptyProgress;
  }

  return usageGeneral - usageLeft;
};

export const prepareExportRewardsJson = (rewards: RewardFieldsBE[]) => {
  return rewards.map((item) => ({
    active: item.active,
    activity: item.activity,
    categories: item.categories,
    costInPoints: item.costInPoints,
    couponGenerator:
      item.reward !== "material" ? item.couponGenerator : undefined,
    couponValue: item.reward === "static_coupon" ? item.couponValue : undefined,
    dateValid: item.reward !== "material" ? item.dateValid : undefined,
    daysInactive: item.reward !== "material" ? item.daysInactive : undefined,
    daysValid: item.reward !== "material" ? item.daysValid : undefined,
    labels: item.labels,
    levels: item.levels,
    name: item.name,
    price: item.price,
    reward: item.reward,
    segments: item.segments,
    sourceWalletTypeCode: item.sourceWalletTypeCode,
    target: item.target,
    translations: transformTranslationsToObject(item.translations),
    unitsConversion:
      item.reward === "conversion_coupon"
        ? {
            ratio: item.unitsConversion?.ratio,
            rounding: item.unitsConversion?.rounding,
          }
        : undefined,
    usageLimit: item.usageLimit,
    visibility: item.visibility,
  }));
};
